import React, { useState, useEffect } from 'react';
import { PageHeader, StatCard, TierBadge, SubTabBar, fmtDollar, fmtCount, fmtMillions } from './shared';
import { apiFetch } from '../api';

function riskTileColor(score) {
  const t = (score - 38) / (81 - 38);
  const r = Math.round(207 * (1 - t));
  const g = Math.round(226 - 166 * t);
  const b = Math.round(255 - 142 * t);
  return { bg: `rgb(${r},${g},${b})`, fg: t > 0.50 ? '#ffffff' : '#003c71' };
}

function riskColor(score) {
  return score >= 80 ? '#dc3545' : score >= 70 ? '#fd7e14' : '#ffc107';
}

function statusClass(s) {
  return s === 'UNASSIGNED' ? 'status-unassigned' : s === 'ASSIGNED' ? 'status-assigned' : 'status-inprogress';
}

function scenarioRiskColor(risk) {
  if (risk >= 70) return '#dc3545';
  if (risk >= 60) return '#fd7e14';
  if (risk >= 50) return '#ffc107';
  return '#198754';
}

function fmtSummaryVal(v) {
  if (typeof v !== 'number') return v;
  return v >= 1_000_000 ? fmtMillions(v) : fmtCount(v);
}

function LoadingRow() {
  return <div className="p-4 text-muted">Loading…</div>;
}

function ErrorRow() {
  return <div className="p-4 text-danger">Failed to load dashboard data.</div>;
}

// ── Views ─────────────────────────────────────────────────────────────────────

function HeatMapView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/predictive-intelligence/heatmap')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Predictive Intelligence  ·  Risk Engine & Geographic Heat Map" memberCount={memberCount} />
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-8">
          <div className="card">
            <div className="card-header fw-semibold">Geographic Risk Heat Map — Markets</div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                {data.marketTiles.map(m => {
                  const { bg, fg } = riskTileColor(m.risk);
                  return (
                    <div key={m.id}
                      style={{ background: bg, borderRadius: 6, padding: '10px 6px', textAlign: 'center', cursor: 'pointer', minHeight: 70 }}
                      title={`Risk Score: ${m.risk} · Members at Risk: ${m.members}`}
                    >
                      <div style={{ color: fg, fontSize: '0.7rem', whiteSpace: 'pre-line', lineHeight: 1.3, fontWeight: m.risk >= 65 ? 700 : 400 }}>{m.label}</div>
                      <div style={{ color: fg, opacity: 0.85, fontSize: '0.65rem', marginTop: 4 }}>{m.members.toLocaleString()} mbrs</div>
                    </div>
                  );
                })}
              </div>
              <div className="d-flex flex-wrap gap-3 mt-3" style={{ fontSize: '0.72rem' }}>
                {[['Very Low <45', 40], ['Low 45–54', 49], ['Moderate 55–64', 59], ['High 65–74', 70], ['Very High ≥75', 79]].map(([l, s]) => {
                  const { bg } = riskTileColor(s);
                  return <span key={l}><span style={{ color: bg, fontWeight: 700 }}>■</span> {l}</span>;
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-4">
          <div className="card h-100">
            <div className="card-header fw-semibold">Summary Stats</div>
            <div className="card-body p-0">
              {data.heatMapSummary.map(r => (
                <div key={r.label} className="d-flex justify-content-between px-3 py-2 border-bottom">
                  <span style={{ fontSize: '0.82rem', color: 'var(--eh-text-mid)' }}>{r.label}</span>
                  <span className="fw-semibold" style={{ fontSize: '0.82rem' }}>{fmtSummaryVal(r.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="card mt-3">
        <div className="card-header fw-semibold">Scenario-Based Intervention Impact Modeling</div>
        <div className="card-body d-flex flex-wrap gap-2">
          {data.heatMapScenarios.map(s => {
            const color = scenarioRiskColor(s.risk);
            return (
              <div key={s.label} className="dash-scenario-card" style={{ borderTop: `4px solid ${color}` }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)', marginBottom: 3 }}>{s.label}</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--eh-navy)' }}>{fmtDollar(s.pmpm)}</div>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color }}>Risk Index: {s.risk}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)', marginTop: 4 }}>{fmtCount(s.members)} members</div>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: s.avoidable === 0 ? '#6c757d' : '#198754', marginTop: 2 }}>Savings Est.: {s.avoidable === 0 ? '$0' : fmtMillions(s.avoidable)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function InterventionQueueView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/predictive-intelligence/queue')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Predictive Intelligence  ·  Member-Level Intervention Queue" memberCount={memberCount} />
      <div className="px-0 mb-2" style={{ fontSize: '0.8rem', color: 'var(--eh-text-mid)' }}>
        <span style={{ color: 'var(--eh-navy)', fontWeight: 600 }}>▶ Drill-down from:</span>
        {' '}Geographic Heat Map › Market A (San Jose) › High-Risk Queue
        <button className="btn btn-link btn-sm p-0 ms-3" style={{ fontSize: '0.78rem' }}>← Back to Heat Map</button>
      </div>
      <div className="dash-stats-row">
        {data.queueKpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>

      <div className="card">
        <div className="card-header fw-semibold">Care Management Intervention Queue — Prioritized by Risk × Opportunity Score</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm mb-0 dash-table">
              <thead>
                <tr><th>Member ID</th><th>Risk</th><th>Primary Driver</th><th>Predicted Event</th><th>Window</th><th>Avoid. $</th><th>Pathway</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {data.interventionQueue.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.id}</td>
                    <td><span className="dash-risk-score" style={{ background: riskColor(r.score) }}>{r.score}</span></td>
                    <td>{r.driver}</td><td>{r.event}</td><td>{r.window}</td>
                    <td className="fw-semibold">{fmtDollar(r.avoidable)}</td>
                    <td>{r.pathway}</td>
                    <td><span className={`dash-status-badge ${statusClass(r.status)}`}>{r.status}</span></td>
                    <td>
                      <button className="btn btn-outline-primary" style={{ fontSize: '0.7rem', padding: '0.1rem 0.45rem' }}>
                        {r.status === 'UNASSIGNED' ? 'Assign' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-header fw-semibold d-flex align-items-center gap-3">
          Member Detail — {data.memberDetail.id}
          <span className="dash-risk-score" style={{ background: '#dc3545', fontSize: '0.75rem' }}>Risk {data.memberDetail.riskScore} — {data.memberDetail.riskLabel}</span>
        </div>
        <div className="card-body py-2">
          <div className="d-flex flex-wrap gap-2 align-items-center pb-2 mb-2 border-bottom" style={{ fontSize: '0.82rem' }}>
            <span className="fw-semibold">{data.memberDetail.name}</span>
            <span style={{ color: 'var(--eh-text-mid)' }}>· Age {data.memberDetail.age} · {data.memberDetail.lob} · {data.memberDetail.market}</span>
            <span className="ms-2" style={{ color: 'var(--eh-text-mid)' }}><strong style={{ color: 'inherit' }}>PCP:</strong> {data.memberDetail.pcp} ({data.memberDetail.pcpStatus}) &nbsp;·&nbsp; <strong style={{ color: 'inherit' }}>LOB:</strong> {data.memberDetail.lob}</span>
          </div>
          <div className="row g-3">
            <div className="col-12 col-md-6 col-xl-3">
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--eh-navy)', marginBottom: 4 }}>CONDITIONS</div>
              {data.memberConditions.map(c => (
                <div key={c} style={{ fontSize: '0.8rem', padding: '2px 0' }}>• {c}</div>
              ))}
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--eh-navy)', marginBottom: 4 }}>SDOH FLAGS</div>
              {data.memberSdoh.map(s => (
                <div key={s} style={{ fontSize: '0.8rem', color: '#dc3545', padding: '2px 0' }}>{s}</div>
              ))}
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--eh-navy)', marginBottom: 4 }}>UTILIZATION VS. BENCHMARK</div>
              {data.memberUtilization.map(u => (
                <div key={u.label} className="d-flex justify-content-between" style={{ fontSize: '0.78rem', padding: '2px 0' }}>
                  <span style={{ color: 'var(--eh-text-mid)' }}>{u.label}</span>
                  <span className="fw-semibold ms-2" style={{ color: u.high ? '#dc3545' : '#198754' }}>
                    {u.value} <span style={{ fontWeight: 400, color: 'var(--eh-text-mid)' }}>(avg {u.avg})</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--eh-navy)', marginBottom: 4 }}>RECENT EVENTS</div>
              {data.recentEvents.map((e, i) => (
                <div key={i} className="d-flex gap-2" style={{ fontSize: '0.75rem', marginBottom: 4 }}>
                  <div style={{ color: 'var(--eh-text-mid)', whiteSpace: 'nowrap', flexShrink: 0 }}>{e.date}</div>
                  <div>
                    <span className="fw-semibold" style={{ color: 'var(--eh-navy)' }}>{e.type}</span>
                    {' — '}{e.desc}
                    <div style={{ color: 'var(--eh-text-mid)', fontSize: '0.7rem' }}>{e.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function SpecialistAlignmentView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/predictive-intelligence/alignment')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Predictive Intelligence  ·  At-Risk Member → High-Performing Specialist Alignment" memberCount={memberCount} />
      <div className="px-0 mb-2" style={{ fontSize: '0.8rem', color: 'var(--eh-text-mid)' }}>
        <span style={{ color: 'var(--eh-navy)', fontWeight: 600 }}>▶ Linked from:</span>
        {' '}Intervention Queue › High-Risk Unassigned Members › Align to Specialist
      </div>
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-5">
          <div className="card">
            <div className="card-header fw-semibold">Member Queue Awaiting Alignment</div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 dash-table">
                  <thead>
                    <tr><th>Member ID</th><th>Risk</th><th>Condition</th><th>Specialty Need</th><th>Window</th><th>Avoid. $</th></tr>
                  </thead>
                  <tbody>
                    {data.alignmentMembers.map(r => (
                      <tr key={r.id}>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.id}</td>
                        <td><span className="dash-risk-score" style={{ background: riskColor(r.score) }}>{r.score}</span></td>
                        <td style={{ fontSize: '0.78rem' }}>{r.cond}</td>
                        <td style={{ fontSize: '0.78rem' }}>{r.specialty}</td>
                        <td style={{ fontSize: '0.75rem' }}>{r.window}</td>
                        <td className="fw-semibold">{fmtDollar(r.avoidable)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2" style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>Showing 7 of {fmtCount(data.memberCount)} members</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-7">
          <div className="card">
            <div className="card-header fw-semibold">Recommended Specialists — Top Tier 1 Matches</div>
            <div className="card-body p-0">
              {data.recommendedSpecialists.map(s => (
                <div key={s.name} className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom gap-2">
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: s.pct >= 90 ? '#198754' : s.pct >= 80 ? '#0067a0' : '#4a6475', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>{s.pct}%</div>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: '0.82rem' }}>{s.name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>{s.spec} · O/E: {s.oe} · {s.eps} eps · {s.dist}</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <TierBadge tier={s.tier} />
                    <button className="btn btn-sm btn-primary" style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem' }}>Assign</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-sm btn-success">Assign All Tier 1 Matches</button>
            <button className="btn btn-sm btn-outline-primary">Export to CM Workflow</button>
            <button className="btn btn-sm btn-outline-secondary">Flag for Review</button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const SUBTABS = [
  { key: 'heatmap',   label: 'Risk Heat Map'        },
  { key: 'queue',     label: 'Intervention Queue'   },
  { key: 'alignment', label: 'Specialist Alignment' },
];

const VIEW_COMPONENTS = {
  heatmap:   HeatMapView,
  queue:     InterventionQueueView,
  alignment: SpecialistAlignmentView,
};

export default function PredictiveIntelligence() {
  const [sub, setSub] = useState('heatmap');
  const [memberCount, setMemberCount] = useState(null);
  const ViewComponent = VIEW_COMPONENTS[sub];
  useEffect(() => {
    apiFetch('/api/dashboards/intelligence-config')
      .then(r => r.json())
      .then(d => setMemberCount(d.membership_count))
      .catch(() => {});
  }, []);
  return (
    <div className="dash-tab-content">
      <SubTabBar tabs={SUBTABS} active={sub} onChange={setSub} />
      <ViewComponent memberCount={memberCount} />
    </div>
  );
}
